import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import StatusBadge from '../components/StatusBadge';
import TaskSubNav from '../components/TaskSubNav';
import { DSPageHeader, DSButton, DSInput, DSSelect, DSCountIndicator, DSIcon, DSBadge } from '../design-system/primitives';
import './taskManagementShared.css';
import './TaskList.css';

const FILTERS = [
  { key: 'all', label: 'Open' },
  { key: 'my_tasks', label: 'My Tasks' },
  { key: 'due_soon', label: 'Due Soon' },
  { key: 'bounty', label: 'Bounty' },
  { key: 'high_points', label: 'High Points' }
];

const STATUS_VARIANT = {
  open: 'success',
  in_progress: 'info',
  completed: 'info',
  pending_confirmation: 'warning',
  confirmed: 'success',
  overdue: 'error',
  auto_closed: 'neutral',
  cancelled: 'neutral'
};

function TaskList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const activeFilter = searchParams.get('filter') || 'all';
  const [categories, setCategories] = useState([]);
  const categoryFilter = searchParams.get('category') || '';
  const showAssigned = searchParams.get('assigned') === 'true';
  const [spotlight, setSpotlight] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  const updateParam = (key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
  };

  const setActiveFilter = (key) => updateParam('filter', key === 'all' ? '' : key);
  const setCategoryFilter = (id) => updateParam('category', id);
  const setShowAssigned = (value) => updateParam('assigned', value ? 'true' : '');

  const isDelegate = currentUser?.role === 'admin' || (currentUser?.featureRoles || []).includes('task_management');

  useEffect(() => {
    setCurrentUser(authService.getCurrentUser());
    apiService.getTaskCategories().then((res) => setCategories(res.data || [])).catch(() => {});
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (activeFilter === 'my_tasks') {
        params.assignedToMe = true;
      } else if (activeFilter === 'due_soon') {
        params.sort = 'due_date';
        params.order = 'asc';
      } else if (activeFilter === 'bounty') {
        params.hasBounty = true;
        params.sort = 'bounty';
      } else if (activeFilter === 'high_points') {
        params.sort = 'points';
      }
      if (search) {
        params.search = search;
      }
      if (categoryFilter) {
        params.categoryId = categoryFilter;
      }
      params.scope = (showAssigned || activeFilter === 'my_tasks') ? 'all' : 'mine_and_open';
      const response = await apiService.getTasks(params);
      setTasks(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, categoryFilter, showAssigned]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadTasks();
      updateParam('q', search);
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    apiService.getTaskSpotlight().then((res) => setSpotlight(res.data?.spotlight || null)).catch(() => {});
    apiService.getMyTaskStats().then((res) => setMyStats(res.data)).catch(() => {});
  }, []);

  const formatDate = (value) => {
    if (!value) {
      return null;
    }
    return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

  const spotlightNames = useMemo(() => {
    if (!spotlight || !spotlight.winners || spotlight.winners.length === 0) {
      return '';
    }
    return spotlight.winners.map((w) => `${w.first_name} ${w.last_name}`).join(' & ');
  }, [spotlight]);

  const activeFilterCount = (activeFilter !== 'all' ? 1 : 0) + (categoryFilter ? 1 : 0) + (showAssigned ? 1 : 0);
  const activeCategoryName = categories.find((c) => String(c.id) === String(categoryFilter))?.name;

  return (
    <div className="tm-page task-list-page">
      <DSPageHeader title="Club Tasks" />

      {spotlight && spotlightNames && (
        <div className="tm-spotlight-banner">
          <span className="tm-spotlight-label"><DSIcon name="star" /> Top Contributor</span>
          <span className="tm-spotlight-title">{spotlightNames} crushed it!</span>
          <span>{spotlight.pointsTotal} points last month</span>
        </div>
      )}

      {myStats?.monthlyStanding && (
        <div className="tm-percentile-banner">
          <DSIcon name="sparkles" /> You're in the top {myStats.monthlyStanding.topPercent}% of contributors this month
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="tm-toolbar">
        <button
          type="button"
          className={`tm-filter-toggle-btn ${activeFilterCount > 0 ? 'active' : ''}`}
          onClick={() => setShowFilterSheet(true)}
          aria-label="Filter tasks"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <DSCountIndicator count={activeFilterCount} tone="brand" label="active filters" />
        </button>
        <DSInput
          type="search"
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <DSButton
          appearance="solid"
          tone="primary"
          className="tm-add-task-btn"
          onClick={() => navigate(isDelegate ? '/tasks/new' : '/tasks/suggest')}
        >
          {isDelegate ? '+ Add' : '+ Suggest'}
        </DSButton>
      </div>

      {activeFilterCount > 0 && (
        <div className="tm-active-filter-chips">
          {activeFilter !== 'all' && (
            <DSBadge variant="neutral" size="sm">
              {FILTERS.find((f) => f.key === activeFilter)?.label}
              <button type="button" className="tm-chip-clear"
                      onClick={() => setActiveFilter('all')} aria-label="Clear filter">
                <DSIcon name="x" />
              </button>
            </DSBadge>
          )}
          {categoryFilter && activeCategoryName && (
            <DSBadge variant="neutral" size="sm">
              {activeCategoryName}
              <button type="button" className="tm-chip-clear"
                      onClick={() => setCategoryFilter('')} aria-label="Clear category">
                <DSIcon name="x" />
              </button>
            </DSBadge>
          )}
          {showAssigned && (
            <span className="tm-active-chip">
              Assigned tasks
              <button type="button" onClick={() => setShowAssigned(false)} aria-label="Clear assigned filter">×</button>
            </span>
          )}
        </div>
      )}

      {showFilterSheet && (
        <div className="tm-sheet-overlay" onClick={() => setShowFilterSheet(false)}>
          <div className="tm-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="tm-sheet-header">
              <h2>Filter Tasks</h2>
              <button type="button" className="tm-sheet-close" onClick={() => setShowFilterSheet(false)} aria-label="Close">×</button>
            </div>

            <div className="tm-filter-pill-row">
              {FILTERS.map((filter) => (
                <DSButton
                  key={filter.key}
                  appearance={activeFilter === filter.key ? 'solid' : 'outline'}
                  tone="primary"
                  size="sm"
                  onClick={() => setActiveFilter(filter.key)}
                >
                  {filter.label}
                </DSButton>
              ))}
            </div>

            <div className="tm-form-group">
              <DSSelect
                id="task-category-filter"
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All categories</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </DSSelect>
            </div>

            <label className="tm-show-assigned-toggle tm-show-assigned-toggle-dark">
              <input type="checkbox" checked={showAssigned} onChange={(e) => setShowAssigned(e.target.checked)} />
              {' '}Show assigned tasks
            </label>

            <DSButton appearance="solid" tone="primary" onClick={() => setShowFilterSheet(false)}>
              Show Results
            </DSButton>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-inline">Loading tasks...</div>
      ) : tasks.length === 0 ? (
        <div className="tm-empty-state">
          {activeFilter === 'my_tasks' ? (
            <>
              <h2>Nothing assigned to you yet</h2>
              <p>Grab something from the open pool, or suggest a task that needs doing.</p>
              <DSButton appearance="solid" tone="primary" onClick={() => setActiveFilter('all')}>
                Browse Open Tasks
              </DSButton>
            </>
          ) : (
            <>
              <h2>All clear! Nice work! 🎉</h2>
              <p>There are no open tasks right now. Check back later, or be the first to flag something that needs doing.</p>
              <DSButton appearance="solid" tone="primary" onClick={() => navigate('/tasks/suggest')}>
                Suggest a Task
              </DSButton>
            </>
          )}
        </div>
      ) : (
        <div className="tm-card-list">
          {tasks.map((task) => {
            const assignees = task.assignees || [];
            const isAssignedToMe = assignees.some((a) => a.userId === currentUser?.id);
            const otherNames = assignees.filter((a) => a.userId !== currentUser?.id).map((a) => a.name);
            let assignedLabel = null;
            if (assignees.length > 0) {
              if (isAssignedToMe && otherNames.length === 0) {
                assignedLabel = 'Assigned to you';
              } else if (isAssignedToMe) {
                assignedLabel = `Assigned to you + ${otherNames.join(', ')}`;
              } else {
                assignedLabel = `Assigned to ${otherNames.join(', ')}`;
              }
            }
            return (
              <Link key={task.id} to={`/tasks/${task.id}`} className="tm-task-card">
                <div className="tm-task-card-main">
                  <h3>{task.title}</h3>
                  <div className="tm-task-tags">
                    {activeFilter === 'my_tasks' && (
                      <StatusBadge label={task.status.replace('_', ' ')} variant={STATUS_VARIANT[task.status] || 'neutral'} uppercase />
                    )}
                    {task.category_name && (
                      <StatusBadge variant="info" size="sm" leadingIcon={<DSIcon name="tag" />}>{task.category_name}</StatusBadge>
                    )}
                    <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="star" />}>{task.point_value}pt{task.point_value === 1 ? '' : 's'}</DSBadge>
                    {task.bounty_amount && (
                      <StatusBadge variant="warning" size="sm" leadingIcon={<DSIcon name="dollar" />}>{formatCurrency(task.bounty_amount)} bounty</StatusBadge>
                    )}
                    {task.due_date && <StatusBadge variant="neutral" size="sm" leadingIcon={<DSIcon name="calendar" />}>Due {formatDate(task.due_date)}</StatusBadge>}
                    {assignedLabel && (
                      <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="user" />}>{assignedLabel}</DSBadge>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
          <button type="button" className="tm-suggest-card" onClick={() => navigate('/tasks/suggest')}>
            + Suggest a task
          </button>
        </div>
      )}
      <TaskSubNav />
    </div>
  );
}

export default TaskList;
